import * as React from 'react';
import * as qs from 'query-string';
import { useLocation, useHistory } from 'react-router';
import { LocationListener, Location } from 'history';

type QsVal = string | string[] | undefined | null;

/**
 * Returns a state variable and callback method of reading and updating a querystring value
 * @param key querystring key
 * @param initial initial value (only set if the key doesn't current have a value in the qs)
 * @returns [string, (value: string) => void] state value and callback for updating the value
 */
const useQueryStringVal = (key: string, initial?: string): [QsVal, (val: QsVal) => void] => {
    const { search } = useLocation();
    const history = useHistory();
    const searchObj = qs.parse(search);

    const [value, setValue] = React.useState<QsVal>(searchObj[key] || initial);
    const onSetValue = React.useCallback(
        (newValue) => {
            setValue(newValue);
            const curValues = qs.parse(window.location.search);
            const newValues = qs.stringify({ ...curValues, [key]: newValue });
            history.push({ search: newValues });
        },
        [key]
    );

    // Listen for history changes and update state variable if the relevant key changes
    const historyListener = React.useCallback(
        (location: Location<any>) => {
            const curValues = qs.parse(location.search);
            const curVal = curValues[key];
            setValue(curVal);
        },
        [key]
    );

    React.useEffect(() => history.listen(historyListener), []);

    return [value, onSetValue];
};

export default useQueryStringVal;
